import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  output,
  viewChild,
  input,
  inject,
  effect,
  untracked,
  signal,
} from '@angular/core';
import { ClrDatagrid, ClrForm } from '@clr/angular';
import { NgForm } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { AutofocusDirective } from 'src/app/directives/autofocus.directive';
import { ItemsMode, WartungsItem } from '../../wartung.types';
import {
  HilfeBeschreibung,
  HilfeBeschreibungModalComponent,
} from 'src/app/content/_components/_modals/hilfebeschreibungmodal/hilfebeschreibungmodal.component';
import { Table } from 'src/app/models/ui/table';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { CommonModule } from '@angular/common';
import { ClarityModule } from '@clr/angular';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import { ConfirmService } from 'src/app/services/confirm.service';

@Component({
  selector: 'app-wartungsitem',
  templateUrl: './wartungsitem.component.html',
  styleUrls: ['./wartungsitem.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    HilfeBeschreibungModalComponent,
    AutofocusDirective,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WartungsItemComponent implements OnInit {
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly store = inject(BasicStoreService);
  private readonly api = inject(WartungApiService);
  private readonly confirmService = inject(ConfirmService);

  readonly mode = input<ItemsMode>(undefined);
  readonly withHelper = input<boolean>(undefined);
  readonly refresh = input<Subject<void>>(undefined);
  readonly otypid = input.required<number>();

  readonly deletedItem = output<number>();
  readonly editedItem = output<{ id: number; name: string }>();

  private readonly clrForm = viewChild(ClrForm);
  private readonly ngForm = viewChild(NgForm);
  private readonly grid = viewChild('wItemGrid', { read: ClrDatagrid });
  private readonly gridContainer = viewChild('gridContainer', { read: ElementRef });

  protected readonly isHelperModalOpen = signal(false);

  constructor() {
    effect(() => {
      if (this.otypid())
        untracked(() => {
          if (this.mode()) this.loadTable();
          if (this.table()) this.table.update(table => ({ ...table, rows: [] }));
          this.isLoading.set(true);
        });
    });
    effect(() => {
      if (!this.isLoading())
        untracked(() => {
          this.grid()?.selection.clearSelection();
          this.insertItem.set(false);
        });
    });
  }

  public set selectedID(value: number) {
    if (value) {
      this.grid().selected = [this.table()?.rows?.find(row => row.ID == value)];
    } else {
      this.grid()?.selection.clearSelection();
    }
  }
  //#region item-Table
  protected readonly table = signal<Table<WartungsItem>>(undefined);

  public selID: number;
  public get selName(): string {
    return this.selectedItem?.Bezeichnung;
  }

  private _selectedItem: WartungsItem;
  protected get selectedItem(): WartungsItem {
    return this._selectedItem;
  }
  protected set selectedItem(value: WartungsItem) {
    this._selectedItem = value;
    this.selID = value?.ID;
    if (value) {
      this.insertItem.set(false);
      setTimeout(() => {
        this.gridContainer()
          .nativeElement.querySelector('#i-' + value.ID)
          ?.scrollIntoView({
            behavior: 'auto',
            block: 'center',
          });
      });
    }
    value ||= { Bezeichnung: '', Typ: 1 };
    this.ngForm()?.resetForm(value);
  }
  //#endregion
  protected insertItem = signal<boolean>(false);
  protected isLoading = signal<boolean>(true);

  //#region load()
  ngOnInit() {
    this.loadTable();
  }

  private async loadTable() {
    const table = await firstValueFrom(this.api.getWartungsItem<WartungsItem>(this.mode(), this.otypid()));
    this.table.set(table);
    this.isLoading.set(false);
  }
  //#endregion
  //#region item()
  protected addItem() {
    this.insertItem.set(true);
    this.grid()?.selection.clearSelection();
  }

  protected saveIfValid() {
    this.clrForm().markAsTouched();
    if (this.ngForm().valid) this.saveItem();
  }

  private async saveItem() {
    const sendObj = { ID: this.selID, ...this.ngForm().value };

    const { success, error, ...neuRow } = await firstValueFrom(
      this.api.setWartungsItem(this.mode(), this.otypid(), sendObj)
    );

    if (success) {
      this.refresh()?.next();
      this.store.deleteComponentStoreAsync('wartung');
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      const neuItem: WartungsItem = { ...this.selectedItem, ...sendObj, ...neuRow };
      if (this.insertItem()) this.table.update(table => (table.rows.unshift(neuItem), { ...table }));
      else {
        this.table.update(
          table => (table.rows.splice(this.table().rows.indexOf(this.selectedItem), 1, neuItem), { ...table })
        );
        if (this.selectedItem.Bezeichnung != neuItem.Bezeichnung)
          this.editedItem.emit({ id: this.selID, name: neuItem.Bezeichnung });
      }
      this.grid().selected = [neuItem];
    } else {
      this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteItem() {
    const text = this.mrTranslate.transform('Eintrag wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (!ok) return;

    const { success, error } = await firstValueFrom(this.api.deleteWartungsItem(this.mode(), this.selID));

    if (success) {
      this.refresh()?.next();
      this.store.deleteComponentStoreAsync('wartung');
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.table.update(table => (table.rows.splice(this.table().rows.indexOf(this.selectedItem), 1), { ...table }));
      this.deletedItem.emit(this.selID);
    } else {
      this.toastr.error(error, this.mrTranslate.transform('Fehler beim Löschen'));
    }
  }

  protected async setAutoHeaders() {
    let all_success = true;
    const tmp_rows = [...this.table().rows];
    for (const row of tmp_rows) {
      if (!row.Bezeichnung.endsWith('::') || row.Typ == 0) continue;

      const headerRow: WartungsItem = { ...row, Typ: 0 };
      const { success } = await firstValueFrom(this.api.setWartungsItem(this.mode(), this.otypid(), headerRow));

      if (success) {
        tmp_rows.splice(tmp_rows.indexOf(row), 1, headerRow);
      } else {
        all_success = false;
        break;
      }
    }

    if (all_success) {
      this.table.update(table => ({ ...table, rows: tmp_rows }));
      this.refresh()?.next();
      this.store.deleteComponentStoreAsync('wartung');
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
    } else
      this.toastr.error(
        this.mrTranslate.transform('Refreshen Sie den Wartungsbaum'),
        this.mrTranslate.transform('Etwas ist schief gelaufen')
      );
  }
  //#endregion
  //#region helper()
  protected async saveHelper(helper: HilfeBeschreibung) {
    const neuItem: WartungsItem = {
      ...this.selectedItem,
      HelpBeschreibung: helper.HelpBeschreibung,
      FileTyp: helper.FileTyp,
    };
    this.table.update(
      table => (table.rows.splice(this.table().rows.indexOf(this.selectedItem), 1, neuItem), { ...table })
    );
    this.grid().selected = [neuItem];
  }
  //#endregion
}
